import "../assets/css/buses.css";
import { useState, useEffect } from 'react';
import { useNotifyHook } from '../hooks/useNotify';
import { config } from "../../config";

let globalSelectedCount = 0;
let globalSelectedSeats = new Set();

const SeatComponent = ({ seat = {}, isSelected = false, onSelect = null }) => {
    const type = seat.type || 'empty';
    const gender = seat.gender || 'empty';
    const no = seat.seat_number || '';
    const deck = seat.deck || '';
    const position = seat.position || '';
    const id = seat.id || '';
    const orientation = seat.orientation || 'vertical';
    const status = seat.status || 'available'; 

    const [selected, setSelected] = useState(isSelected);
    const notify = useNotifyHook();

    // Sync with parent component
    useEffect(() => {
        setSelected(isSelected);
        if (isSelected && !globalSelectedSeats.has(id)) {
            globalSelectedSeats.add(id);
            globalSelectedCount++;
        } else if (!isSelected && globalSelectedSeats.has(id)) {
            globalSelectedSeats.delete(id);
            globalSelectedCount--;
        }
    }, [isSelected, id]);

    const genderColors = {
        'male': '#2b7fff',
        'female': '#f6339a',
        'unisex': '#9B59B6',
        'empty': '#ad46ff',
    };

    const getColor = () => {
        if (status === 'booked') return '#9ca3af';
        if (selected) return '#10b981';
        return genderColors[gender] || genderColors['empty'];
    };

    const color = getColor();

    const genderClasses = {
        'male': 'seat-male',
        'female': 'seat-female',
        'unisex': 'seat-unisex',
        'empty': 'seat-empty',
    };

    const genderClass = genderClasses[gender] || 'seat-empty';

    const seatDimensions = {
        'seating': { width: '50px', height: '30px' },
        'sleeper': { width: '50px', height: '70px' },
        'semi_sleeper': { width: '50px', height: '50px' },
        'empty': { width: '50px', height: '40px' },
    };

    const dimensions = seatDimensions[type] || seatDimensions['empty'];

    const handleClick = (e) => {
        e.stopPropagation();

        if (status === 'booked') return;
        if (type === 'empty') return;

        if (selected) {
            // Deselect seat
            const newSelected = false;
            setSelected(newSelected);
            globalSelectedSeats.delete(id);
            globalSelectedCount--;

            if (onSelect) {
                onSelect(id, newSelected, seat);
            }

            if (window.fillForUpdateSeat) {
                window.fillForUpdateSeat(id, no, deck, position, type, gender);
            }
        } else {
            // Try to select seat
            if (globalSelectedCount < config.maxSeatSelection) {
                const newSelected = true;
                setSelected(newSelected);
                globalSelectedSeats.add(id);
                globalSelectedCount++;

                if (onSelect) {
                    onSelect(id, newSelected, seat);
                }

                if (window.fillForUpdateSeat) {
                    window.fillForUpdateSeat(id, no, deck, position, type, gender);
                }
            } else {
                notify.warning(`You can only select a maximum of 6 seats. You have already selected ${globalSelectedCount} seats.`);
            }
        }
    };

    const getRotation = () => {
        if (orientation === 'horizontal') {
            return { transform: 'rotate(90deg)', transformOrigin: 'center' };
        }
        return {};
    };

    const formatFare = (fare) => {
        if (!fare && fare !== 0) return '0';
        const numFare = typeof fare === 'number' ? fare : parseFloat(fare);
        if (isNaN(numFare)) return '0';
        const formattedFare = numFare.toFixed(2);
        return formattedFare.endsWith('.00') ? formattedFare.slice(0, -3) : formattedFare;
    };

    const getSeatClasses = () => {
        const baseClass = `seat ${genderClass} ${type === "sleeper" ? 'sleeper-seat' : ''}`;
        if (selected) return `${baseClass} seat-selected`;
        return baseClass;
    };

    // Reset function (optional, can be called from parent)
    const resetSelection = () => {
        globalSelectedCount = 0;
        globalSelectedSeats.clear();
    };

    const renderSeating = () => (
        <div className="bus">
            <div id={`seat-${no}`} className="scolumn">
                <svg
                    width="97" height="50" viewBox="0 0 97 50"
                    fill={color}
                    xmlns="http://www.w3.org/2000/svg"
                    className="seat1b1"
                    style={getRotation()}
                >
                    <g clipPath="url(#clip0_229_21)">
                        <path
                            d="M81.65 81H15.33C12.87 81 10.87 79 10.87 76.54V10.22C10.87 7.75999 12.87 5.75999 15.33 5.75999H81.65C84.11 5.75999 86.11 7.75999 86.11 10.22V76.54C86.11 79 84.12 81 81.65 81Z"
                            stroke="black"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M17.33 81H8.19997C6.61997 81 5.33997 79.72 5.33997 78.14V30.74C5.33997 29.16 6.61997 27.89 8.19997 27.89H14.66C16.24 27.89 17.51 29.17 17.51 30.74V80.82C17.51 80.92 17.43 81 17.33 81Z"
                            fill="white"
                            stroke="black"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path
                            d="M88.79 81H79.73C79.59 81 79.48 80.89 79.48 80.75V30.75C79.48 29.17 80.76 27.89 82.34 27.89H88.79C90.37 27.89 91.65 29.17 91.65 30.75V78.14C91.65 79.72 90.37 81 88.79 81Z"
                            fill="white"
                            stroke="black"
                            strokeWidth="0.75"
                            strokeMiterlimit="10"
                        />
                        <path d="M17.51 69.94H79.48" stroke="black" strokeWidth="0.75" strokeMiterlimit="10" />
                    </g>
                    <defs>
                        <clipPath id="clip0_229_21">
                            <rect
                                width={orientation === 'vertical' ? "97" : "84"}
                                height={orientation === 'vertical' ? "84" : "97"}
                                fill="white"
                            />
                        </clipPath>
                    </defs>
                </svg>
                <div className="seat-number seatlook12 sold seat" data-price="1">
                    {formatFare(seat.fare)}
                </div>
            </div>
        </div>
    );

    const renderSleeper = () => (
        <svg
            width="45" height="70" viewBox="0 0 60 100"
            xmlns="http://www.w3.org/2000/svg"
        >
            {orientation === 'vertical' ? (
                <>
                    <rect x="5" y="5" width="50" height="85" rx="5" fill={color} stroke="#333" strokeWidth="1.5" />
                    <rect x="15" y="10" width="30" height="12" rx="6" fill={color} stroke="#333" strokeWidth="0.5" />
                    <rect x="15" y="10" width="30" height="12" rx="6" fill="white" />
                    <text
                        x="30"
                        y="60"
                        fontSize="14"
                        fill="white"
                        fontWeight="bold"
                        transform="rotate(-180 35 60)"
                    >
                        {formatFare(seat.fare)}
                    </text>
                </>
            ) : (
                <>
                    <rect x="5" y="5" width="85" height="50" rx="5" fill={color} stroke="#333" strokeWidth="1.5" />
                    <rect x="10" y="15" width="12" height="30" rx="6" fill={color} stroke="#333" strokeWidth="0.5" />
                    <rect x="10" y="15" width="12" height="30" rx="6" fill="white" />
                    <text
                        x="50"
                        y="30"
                        textAnchor="middle"
                        fontFamily="Arial"
                        fontSize="14"
                        fill="white"
                        fontWeight="bold"
                    >
                        {formatFare(seat.fare)}
                    </text>
                </>
            )}
        </svg>
    );

    const renderEmpty = () => {
        const svgWidth = orientation === 'vertical' ? 50 : 60;
        const svgHeight = orientation === 'vertical' ? 60 : 50;

        return (
            <svg
                width={svgWidth}
                height={svgHeight}
                viewBox="0 0 97 84"
                xmlns="http://www.w3.org/2000/svg"
                style={getRotation()}
            >
                {orientation === 'vertical' ? (
                    <>
                        <rect x="5" y="10" width="40" height="30" rx="5" fill="#ECF0F1" stroke="#CCC" strokeWidth="1.5" strokeDasharray="3" />
                        <text x="25" y="27" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#999">
                            {no || 'Empty'}
                        </text>
                    </>
                ) : (
                    <>
                        <rect x="10" y="5" width="30" height="40" rx="5" fill="#ECF0F1" stroke="#CCC" strokeWidth="1.5" strokeDasharray="3" />
                        <text x="25" y="27" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#999" transform="rotate(90 25 27)">
                            {no || 'Empty'}
                        </text>
                    </>
                )}
            </svg>
        );
    };

    const renderSeatContent = () => {
        switch (type) {
            case 'seating':
                return renderSeating();
            case 'sleeper':
                return renderSleeper();
            case 'semi_sleeper':
                return renderSeating();
            default:
                return renderEmpty();
        }
    };

    return (
        <div
            className={getSeatClasses()}
            data-type={type}
            data-gender={gender}
            data-no={no}
            data-deck={deck}
            data-position={position}
            data-id={id}
            data-fare={seat.fare}
            data-selected={selected}
            onClick={handleClick}
            style={{
                width: dimensions.width,
                height: dimensions.height,
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                cursor: type !== 'empty' ? 'pointer' : 'default',
                position: 'relative',
            }}
        >
            {renderSeatContent()}
            {selected && (
                <div className="selected-indicator" style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#10b981',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                }}>
                    ✓
                </div>
            )}
        </div>
    );
};

// Export reset function
export const resetSeatSelection = () => {
    globalSelectedCount = 0;
    globalSelectedSeats.clear();
};

export default SeatComponent;